import React from "react";
import { format, isSameDay, isSameMonth } from "date-fns";
import { Button } from "./ui/button";
import { cn } from "../utils/cn";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { WorkoutHistoryItem } from "../services/workout";

interface CalendarGridProps {
  currentDate: Date;
  calendarDays: Date[];
  selectedDate: Date | null;
  workoutHistory: WorkoutHistoryItem[];
  onDateSelect: (date: Date) => void;
  onPreviousMonth: () => void;
  onNextMonth: () => void;
  onGoToToday: () => void;
  getStatusColor: (status: string) => string;
}

export const CalendarGrid: React.FC<CalendarGridProps> = ({
  currentDate,
  calendarDays,
  selectedDate,
  workoutHistory,
  onDateSelect,
  onPreviousMonth,
  onNextMonth,
  onGoToToday,
  getStatusColor,
}) => {
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <Button variant="ghost" size="sm" onClick={onPreviousMonth}>
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <span className="font-semibold">{format(currentDate, "MMMM yyyy")}</span>
        <Button variant="ghost" size="sm" onClick={onNextMonth}>
          <ChevronRight className="h-4 w-4" />
        </Button>
        <Button variant="ghost" size="sm" onClick={onGoToToday}>
          Today
        </Button>
      </div>
      <div className="grid grid-cols-7 gap-2">
        {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day) => (
          <div key={day} className="text-center font-medium text-muted-foreground p-2 text-sm">
            {day}
          </div>
        ))}
        {calendarDays.map((day, i) => {
          const dayWorkouts = workoutHistory.filter((workout) => isSameDay(workout.date, day));
          const hasWorkouts = dayWorkouts.length > 0;
          const isSelected = selectedDate && isSameDay(day, selectedDate);
          const isToday = isSameDay(day, new Date());
          const isCurrentMonth = isSameMonth(day, currentDate);
          return (
            <Button
              key={i}
              variant="ghost"
              className={cn(
                "h-14 w-full relative flex flex-col items-center justify-center p-2 hover:bg-muted rounded-md group",
                !isCurrentMonth && "text-muted-foreground opacity-50",
                isSelected && "bg-primary/10 font-semibold ring-2 ring-primary ring-offset-2 ring-offset-background z-10",
                isToday && !isSelected && "ring-2 ring-indigo-400 ring-offset-2 ring-offset-background z-10",
                hasWorkouts && !isSelected && !isToday && "calendar-has-workout"
              )}
              onClick={() => onDateSelect(day)}
            >
              <span className={cn("text-sm z-20", isToday && "font-bold")}>{format(day, "d")}</span>
              {hasWorkouts && (
                <div className="absolute bottom-1 flex space-x-1 z-20">
                  {dayWorkouts.slice(0, 3).map((workout, idx) => (
                    <div
                      key={idx}
                      className={cn("w-1.5 h-1.5 rounded-full", getStatusColor(workout.status))}
                      title={workout.workout_name}
                    ></div>
                  ))}
                  {dayWorkouts.length > 3 && (
                    <span className="text-xs text-muted-foreground">+{dayWorkouts.length - 3}</span>
                  )}
                </div>
              )}
            </Button>
          );
        })}
      </div>
    </div>
  );
};
